<template>
  <v-form
    ref="form"
    v-model="valid"
    class="defect-info-card-root"
    @submit.prevent="submit"
  >
    <v-card-text class="defect-info-card-body pa-0 px-3 pt-1 pb-1">
      <v-row justify="center" class="ma-0" dense>
        <v-col class="py-0 px-2" cols="6">
          <div class="d-flex justify-center align-center">
            <DrawableImage
              ref="defectMarker"
              :thermal-image="thermalImage"
              :defect-info="defect"
            />
          </div>
        </v-col>
        <v-col class="py-0 px-2" cols="6">
          <ImageZoomViewer
            :key="imageZoomViewerKey"
            ref="imageZoomViewer"
            :img-url="solarRgbImage ? solarRgbImage.imgUrl : ''"
            :img-width="rgbViewerPixelWidth"
            :img-height="rgbViewerPixelHeight"
          />
        </v-col>
      </v-row>

      <v-row class="ma-0" dense>
        <v-col cols="6" class="px-2 py-0">
          <div class="text-subtitle-2 font-weight-medium text--primary mb-1">
            {{ $t('defects.cardSectionManagement') }}
          </div>
          <div class="text-caption defect-info-panel-meta mb-1">
            {{ $t('defects.arrayId') }} {{ panelArrayId }}
            · {{ $t('defects.stringId') }} {{ panelStringId }}
            · {{ $t('defects.moduleId') }} {{ panelModuleId }}
          </div>
          <v-row dense class="ma-0">
            <v-col>
              <v-select
                v-model="mainDefectCode"
                tabindex="0"
                :label="$t('defects.typeLabel')"
                :items="mainDefectSelectOptions"

                item-text="name"
                item-value="id"
                return-object
                outlined
                dense
                hide-details="auto"
                :rules="[(v) => !!v || $t('defects.popup.selectDefectType')]"
                @change="handleMainDefectChange($event)"
              />
            </v-col>
            <v-col>
              <v-select
                v-model="subDefectCode"
                :label="$t('defects.subTypeFull')"
                :items="subDefectSelectOptions"
                item-text="name"
                item-value="id"
                outlined
                dense
                hide-details="auto"
                :disabled="
                  subDefectSelectOptions === null || subDefectSelectOptions.length === 0
                "
                return-object
                @change="handleSubDefectChange($event)"
              />
            </v-col>
            <v-col>
              <v-select
                v-model="defectStatus"
                :label="$t('defects.status')"
                :items="defectStatusOptions"
                item-text="name"
                item-value="code"
                outlined
                dense
                hide-details="auto"
                :rules="[(v) => !!v || $t('defects.formSelectStatus')]"
              />
            </v-col>
          </v-row>
          <div class="text-caption text--secondary mt-1 mb-0">
            {{ $t('defects.detailDescriptionHeading') }}
          </div>
          <div class="text-body-2 defect-desc-readout text--primary">
            {{ selectedDefectDescription }}
          </div>
          <div class="text-overline text--secondary mb-0 mt-1">
            {{ $t('map.thermalStatsSection') }}
          </div>
          <div class="pa-1 pt-1 thermal-stats-compact">
            <div class="d-flex flex-wrap align-center justify-space-between thermal-stats-compact-row">
              <span class="thermal-stats-compact-item d-flex align-center flex-shrink-0">
                <v-icon small color="error">
                  mdi-thermometer-high
                </v-icon>
                <span class="text-caption text--secondary ml-1">{{ $t('map.thermalStatMax') }}</span>
                <span class="thermal-stats-compact-value font-weight-medium thermal-stat-value--max ml-1">
                  {{ defectThermalDisplayMax }}
                </span>
              </span>
              <span class="thermal-stats-compact-item d-flex align-center flex-shrink-0">
                <v-icon small color="info">
                  mdi-thermometer-low
                </v-icon>
                <span class="text-caption text--secondary ml-1">{{ $t('map.thermalStatMin') }}</span>
                <span class="thermal-stats-compact-value font-weight-medium thermal-stat-value--min ml-1">
                  {{ defectThermalDisplayMin }}
                </span>
              </span>
              <span class="thermal-stats-compact-item d-flex align-center flex-shrink-0">
                <v-icon small color="success">
                  mdi-chart-line
                </v-icon>
                <span class="text-caption text--secondary ml-1">{{ $t('map.thermalStatAvg') }}</span>
                <span class="thermal-stats-compact-value font-weight-medium thermal-stat-value--avg ml-1">
                  {{ defectThermalDisplayAvg }}
                </span>
              </span>
            </div>
          </div>
        </v-col>
        <v-col cols="6" class="px-2 py-0">
          <div class="text-subtitle-2 font-weight-medium text--primary mb-1">
            {{ $t('common.memo') }}
          </div>
          <v-textarea
            :value="defectDescForDisplay"
            outlined
            dense
            hide-details="auto"
            rows="3"
            @input="updateLocalDefectDesc($event)"
          />
          <div class="d-flex flex-wrap align-center mt-2 pt-2 defect-info-card-actions">
            <v-btn
              outlined
              class="solvit-crud-outline-btn solvit-crud-outline-btn--danger defect-info-card-action-btn ma-1"
              :disabled="!isEdit"
              @click="deleteItem"
            >
              {{ $t('common.delete') }}
            </v-btn>
            <v-spacer />
            <v-btn
              outlined
              class="solvit-crud-outline-btn defect-info-card-action-btn ma-1"
              @click="close"
            >
              {{ $t('common.close') }}
            </v-btn>
            <v-btn
              outlined
              type="submit"
              class="solvit-crud-outline-btn solvit-crud-outline-btn--primary defect-info-card-action-btn ma-1"
            >
              {{ $t('common.save') }}
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
  </v-form>
</template>

<script>
import api from '@/apis/Api';
import DotSolarMap from '../map/DotSolarMap';
const _appDomain = process.env.VUE_APP_DOMAIN ? process.env.VUE_APP_DOMAIN : '';
const _apiUrl = _appDomain + process.env.VUE_APP_API_URL;
import { mapGetters, mapActions, mapMutations } from 'vuex';
import * as d3 from 'd3';
import { syncDefectModuleLinkFromServerLocation } from '@/services/defect/nearModuleResolver';
import { unwrapServiceContent } from '@/utils/defect/unwrapServiceContent';
import { findSolarModuleFeatureInLayer } from '@/utils/map/findSolarModuleFeature';
import { invalidateModuleDefectDetailCache } from '@/utils/moduleDefectDetailCache.js';
import {
  formatDefectPanelContextDisplay,
  resolveDefectPanelContext
} from '@/utils/map/resolveDefectPanelContext';

export default {
  components: {
    DrawableImage: () => import('../card/DrawableImage'),
    ImageZoomViewer: () => import('../../components/image/ImageZoomViewer')
  },
  data: () => ({
    imageZoomViewerKey: 0,
    localDefectDesc: ''
  }),
  computed: {
    defectDescForDisplay() {
      // defectForm의 memo를 우선적으로 사용 (서버의 defectDesc 값이 defectForm에 로드됨)
      const result = this.defectFormMemo || this.defect?.defectDesc || this.localDefectDesc || '';
      return result;
    },
    defectStatusOptions() {
      return [
        { code: 'FOUND', name: this.$t('defects.found') },
        { code: 'SCHEDULE', name: this.$t('defects.actionPlanned') },
        { code: 'COMPLETE', name: this.$t('defects.actionCompleted') }
      ];
    },
    wfsDefectRow() {
      const defectId = this.defect?.id;
      if (defectId == null) {
        return null;
      }
      const rows = this.$store.state.dashboard?.wfsDefectRows || [];
      return rows.find(d => Number(d.seq) === Number(defectId)) || null;
    },
    moduleLayerProps() {
      const seq =
        this.defect?.solarModuleSeq ??
        this.wfsDefectRow?.solarModuleSeq ??
        this.solarModule?.id ??
        this.solarModule?.seq ??
        null;
      if (seq == null) {
        return {};
      }
      const layer = DotSolarMap.workingLayer?.solarModule;
      const feature = findSolarModuleFeatureInLayer(layer, seq);
      return feature?.getProperties?.() || {};
    },
    resolvedPanelContext() {
      return resolveDefectPanelContext({
        defect: this.defect,
        solarModule: this.solarModule || this.defect?.solarModule,
        wfsModuleSeq: this.wfsDefectRow?.solarModuleSeq,
        moduleLayerProps: this.moduleLayerProps
      });
    },
    panelContextDisplay() {
      return formatDefectPanelContextDisplay(this.resolvedPanelContext);
    },
    panelArrayId() {
      return this.panelContextDisplay.arrayId;
    },
    panelStringId() {
      return this.panelContextDisplay.stringId;
    },
    panelModuleId() {
      return this.panelContextDisplay.moduleId;
    },
    ...mapGetters({
      defect: 'defect/getDefect',
      thermalImage: 'defect/getThermalImage',
      mainDefectSelectOptions: 'defect/getMainDefectSelectOptions',
      subDefectSelectOptions: 'defectForm/getSubDefectOptions',
      selectedMainDefect: 'defect/getSelectedMainDefect',
      selectedSubDefect: 'defect/getSelectedSubDefect',
      defectTemperature: 'defect/getDefectTemperature',
      solarRgbImage: 'defect/getRgbImage',
      solarThermalImageId: 'defect/getSolarThermalImageId',
      solarThermalImageName: 'defect/getSolarThermalImageName',
      formValid: 'defectForm/getFormValid',
      selectedImage: 'defect/getSelectedImage',
      solarModule: 'defect/getSolarModule',
      isEdit: 'defect/getIsEdit',
      reportId: 'explorer/getReportId',
      defectMode: 'defect/getDefectMode',
      parentDefectTypes: 'defect/parentDefectTypes',
      defectTypesData: 'defect/defectTypesLoaded',
      // defectForm getters 추가
      defectFormDescription: 'defectForm/getDefectDescription',
      defectFormMemo: 'defectForm/getDefectMemo',
      defectFormStatus: 'defectForm/getDefectFormStatus',
      defectFormMainType: 'defectForm/getDefectFormMainType',
      defectFormSubType: 'defectForm/getDefectFormSubType'
    }),
    mainDefectCode: {
      get() {
        const result = this.defectFormMainType || this.selectedMainDefect || this.mainDefectSelectOptions[1];
        return result;
      },
      set(val) {
        // 상위 타입 변경 시 하위 타입 초기화
        this.updateDefectForm({field: 'mainType', value: val});
        // 하위 타입도 초기화
        this.updateDefectForm({field: 'subType', value: null});
      }
    },
    subDefectCode: {
      get() {
        // defectForm의 하위 타입 우선, defect store의 하위 타입 차선
        const result = this.defectFormSubType || this.selectedSubDefect;
        return result;
      },
      set(val) {
        this.updateDefectForm({field: 'subType', value: val});
      }
    },
    selectedDefectDescription: {
      get() { return this.defectFormDescription || ''; }
    },
    defectStatus: {
      get() {
        return this.defectFormStatus || this.defect?.defectStatus || 'FOUND';
      },
      set(val) {
        this.updateDefectForm({field: 'status', value: val});
      }
    },
    defectDesc: {
      get() {
        // 결함 상세 설명: defectForm의 description (defectCode.description)
        const result = this.defectFormDescription || this.defect?.defectCode?.description || '';
        return result;
      },
      set(val) {
        this.updateDefectForm({field: 'description', value: val});
      }
    },
    solarExamineReportId: {
      get() { return this.reportId; },
      set(val) {
        this.$store.commit('explorer/SET_REPORT_ID', val);
      }
    },
    valid: {
      get() { return this.formValid; },
      set(val) { this.updateUiState({field: 'formValid', value: val}); }
    },
    selectedImage: {
      get() { return this.$store.getters['defect/getSelectedImage']; },
      set(val) { this.UPDATE_SELECTED_IMAGE(val); }
    },
    solarModule: {
      get() { return this.$store.getters['defect/getSolarModule']; },
      set(val) { this.UPDATE_SOLAR_MODULE(val); }
    },
    maxHeat() {
      return this.defectTemperature?.max;
    },
    minHeat() {
      return this.defectTemperature?.min;
    },
    avrHeat() {
      return this.defectTemperature?.avr;
    },
    /** 열화상 패널과 동일 포맷: 값 없음 → '-', 있으면 `n ℃` (0℃도 표시) */
    defectThermalDisplayMax() {
      const v = this.maxHeat;
      if (v === null || v === undefined || v === '') return '-';
      return `${v} ℃`;
    },
    defectThermalDisplayMin() {
      const v = this.minHeat;
      if (v === null || v === undefined || v === '') return '-';
      return `${v} ℃`;
    },
    defectThermalDisplayAvg() {
      const v = this.avrHeat;
      if (v === null || v === undefined || v === '') return '-';
      return `${v} ℃`;
    },
    /**
     * 저장 시 서버로 보낼 solarThermalImage id.
     * 수정 모드에서는 탐색기·다이얼로그 네비가 다른 열화상을 가리켜도 결함이 원래 속한 이미지 ID를 유지한다.
     * 신규 등록은 현재 선택 열화상 ID.
     */
    thermalImageIdForSave() {
      if (this.isEdit) {
        const nested = this.defect?.solarThermalImage;
        const fromDefect =
          nested && typeof nested === 'object' && nested.id != null && nested.id !== ''
            ? nested.id
            : null;
        if (fromDefect != null) {
          return fromDefect;
        }
        if (this.solarThermalImageId != null && this.solarThermalImageId !== '') {
          return this.solarThermalImageId;
        }
      }
      return this.thermalImage?.id ?? null;
    },
    /**
     * ImageZoomViewer 가 natural 크기용으로 같은 JPEG를 한 번 더 받지 않도록,
     * API(solarRgbImage)·열화상에 픽셀 크기가 있으면 전달한다.
     */
    rgbViewerPixelWidth() {
      const rgb = this.solarRgbImage;
      const th = this.thermalImage;
      const w =
        rgb?.imgWidth ??
        rgb?.width ??
        rgb?.pixelWidth ??
        th?.imgWidth ??
        th?.width;
      const n = Number(w);
      return Number.isFinite(n) && n > 0 ? n : null;
    },
    rgbViewerPixelHeight() {
      const rgb = this.solarRgbImage;
      const th = this.thermalImage;
      const h =
        rgb?.imgHeight ??
        rgb?.height ??
        rgb?.pixelHeight ??
        th?.imgHeight ??
        th?.height;
      const n = Number(h);
      return Number.isFinite(n) && n > 0 ? n : null;
    }
  },
  watch: {
    resetFlag: {
      handler(newVal) {
        if (newVal) {
          this.resetForm(this.resetOptions || {});
        }
      },
      immediate: false
    },
    solarExamineReportId: {
      immediate: true,
      deep: true,
      handler(newVal, oldVal) {
        // 콘솔 로그 제거
      }
    }
  },
  mounted() {
    if (this.$eventBus) {
      this.$eventBus.$on('defect-temperature', this.onDefectTemperatureEvent);
    }
  },
  beforeUnmount() {
    if (this.$eventBus) {
      this.$eventBus.$off('defect-temperature', this.onDefectTemperatureEvent);
    }
  },
  methods: {
    ...mapMutations('defect', [
      'SET_SELECTED_SUB_DEFECT_TYPE',
      'UPDATE_DEFECT_STATUS',
      'UPDATE_DEFECT_DESC',
      'UPDATE_SUB_DEFECT_OPTIONS',
      'UPDATE_DEFECT_TEMPERATURE',
      'UPDATE_SOLAR_THERMAL_IMAGE_ID',
      'UPDATE_SOLAR_THERMAL_IMAGE_NAME',
      'UPDATE_VALID',
      'UPDATE_SELECTED_IMAGE',
      'UPDATE_SOLAR_MODULE',

      'UPDATE_IS_EDIT'
    ]),
    ...mapActions('defect', [
      'closeDefectInfoDialog',
      'refreshAfterDefectChange',
      'resetDefectInfoCard'
    ]),
    ...mapActions('defectForm', [
      'updateDefectForm',
      'updateUiState',
      'resetDefectFormData',
      'updateSelectedDefectType'
    ]),
    ...mapMutations('defect', [
      'UPDATE_DEFECT_STATUS',
      'UPDATE_DEFECT_DESC'
    ]),
    onDefectTemperatureEvent(payload) {
      if (payload !== null) {
        this.UPDATE_DEFECT_TEMPERATURE(payload);
      }
    },
    close() {
      this.closeDefectInfoDialog();
      this.resetDefectInfoCard();
      // form validation 상태 초기화
      this.$refs.form?.resetValidation();
    },
    async resetForm(options = {}) {
      //로컬변수 상태 초기화
      this.localDefectDesc = '';

      // Vuex store의 값들을 초기화
      await this.resetDefectInfoCard();

      // defectForm 초기화
      await this.resetDefectFormData();

      // 열화상 네비 직후에는 DrawableImage·DefectDrawer 가 곧 올바른 온도를 emit 하므로 여기서 비우지 않음
      if (!options.preserveDefectTemperature) {
        this.UPDATE_DEFECT_TEMPERATURE({ min: null, max: null, avr: null });
      }

      // form validation 초기화
      this.$refs.form?.resetValidation();

      // ImageZoomViewer 컴포넌트 재생성 (key 변경으로 완전 초기화)
      this.imageZoomViewerKey++;
    },
    validateForm() {
      const isValid = this.$refs.form.validate();
      this.updateUiState({field: 'formValid', value: isValid});
      return isValid;
    },
    extractDefectIdFromSaveResponse(response) {
      const payload = unwrapServiceContent(response?.data ?? response);
      return payload?.id ?? payload?.seq ?? null;
    },
    async finalizeDefectSave(defectId) {
      if (defectId == null) {
        return;
      }
      const thermalImageId =
        this.thermalImageIdForSave ??
        this.solarThermalImageId ??
        this.$store.getters['dashboard/getSelectedThermalImageId'];
      const defectShape = this.$refs['defectMarker']?.markerCoords ?? null;

      try {
        await syncDefectModuleLinkFromServerLocation(defectId, this.$store, {
          trigger: 'defect-info-card-save',
          thermalImageId,
          defectShape
        });
      } catch (error) {
        console.warn('[DEFECT INFO CARD] 저장 후 모듈 연결 동기화 실패:', error);
      }
    },
    submit() {
      if (!this.validateForm()) {
        return;
      }
      this.save();
    },
    async save() {
      const saveData = {
        ...this.defect,
        defectShape: this.$refs['defectMarker'].markerCoords,
        defectDesc: this.defectFormMemo, //defectform의 description은 화면표시용이고 실제로는 메모를 defectDesc에 저장
        defectStatus: this.defectFormStatus || this.defect?.defectStatus,
        memo: this.defectFormMemo // 메모 필드 추가
      };

      console.log('[DEFECT INFO CARD] save - saveData 생성:', {
        defectDesc: saveData.defectDesc,
        memo: saveData.memo,
        defectFormMemo: this.defectFormMemo,
        defectFormDescription: this.defectFormDescription
      });

      console.log('[DEFECT INFO CARD] save - 저장할 데이터:', {
        defectDesc: saveData.defectDesc,
        memo: saveData.memo,
        defectStatus: saveData.defectStatus,
        defectFormDescription: this.defectFormDescription,
        defectFormMemo: this.defectFormMemo,
        defectFormStatus: this.defectFormStatus
      });

      // 결함의 지도상의 좌표위치 계산을 프론트에서 하지 않고 서버에서 계산하도록 변경함에 따라 다음 로직을 주석 처리함.
      // 2026. 03. 18 수정 Yeongjong Kang 
      // if (saveData.detectWay != 'AI') {
      //   saveData.defectLocation = this.$refs['defectMarker'].defectCoords;
      // }

      console.log('[DEFECT INFO CARD] save - solarExamineReportId:', this.solarExamineReportId);
      console.log('[DEFECT INFO CARD] save - thermalImage:', this.thermalImage);
      console.log('[REPORT_ID_TRACK] save 시점의 reportId 값들:', {
        solarExamineReportId: this.solarExamineReportId,
        thermalImageReportId: this.thermalImage?.solarExamineReport,
        storeReportId: this.reportId,
        finalReportId: this.reportId
      });

      const apiSaveData = {
        id: saveData.id,
        detectWay: saveData.detectWay || 'MANUAL',
        defectStatus: saveData.defectStatus,
        defectDesc: saveData.defectDesc,
        memo: saveData.memo, // 메모 필드 추가
        // Spring의 RestData API를 사용하지 않고, 서버의 서비스 로직에서 처리를 함에 따라 URL이 아니라 
        // 열화상이미지/보고서 객체의 식별자 값을 설정하여 반환하도록 수정함.
        // 2026. 03. 18 수정 Yeongjong Kang 
        // solarThermalImage: this.thermalImage?.id ? `/api/solarThermalImages/${this.thermalImage.id}` : null,
        // solarExamineReport: this.reportId ? `/api/solarExamineReports/${this.reportId}` : null,
        solarThermalImage: { id: this.thermalImageIdForSave },
        solarExamineReport: {id: this.reportId},
        defectShape: saveData.defectShape,
        // defectLocation: saveData.defectLocation,
        maxHeat: this.maxHeat,
        minHeat: this.minHeat,
        averageHeat: this.avrHeat,
        mlDefectConfirmed: saveData.mlDefectConfirmed
      };

      console.log('[REPORT_ID_TRACK] API 저장 데이터:', apiSaveData);
      console.log('[DEFECT-POSITION-DEBUG] 카드 저장 PUT 요약(서버가 defectShape로 지도점 계산)', {
        defectId: apiSaveData.id,
        defectShape: apiSaveData.defectShape,
        hasDefectCode: !!apiSaveData.defectCode,
        defectCodeId: apiSaveData.defectCode?.id
      });

      // 결함 코드 설정 (defectForm 데이터 우선 사용)
      console.log('[DEFECT INFO CARD] save - 결함 코드 설정 전:', {
        defectFormMainType: this.defectFormMainType,
        defectFormSubType: this.defectFormSubType,
        mainDefectCode: this.mainDefectCode,
        subDefectCode: this.subDefectCode
      });

      if (this.defectFormMainType || this.defectFormSubType || this.mainDefectCode || this.subDefectCode) {
        const defectCodeId = this.defectFormSubType ? this.defectFormSubType.id :
          this.defectFormMainType ? this.defectFormMainType.id :
          this.subDefectCode ? this.subDefectCode.id :
          this.mainDefectCode ? this.mainDefectCode.id : null;

        console.log('[DEFECT INFO CARD] save - 선택된 결함 코드 ID:', defectCodeId);

        if (defectCodeId) {
          // apiSaveData.defectCode = `/api/defectTypes/${defectCodeId}`;
          apiSaveData.defectCode = {id: defectCodeId};
          console.log('[DEFECT INFO CARD] save - 설정된 결함 코드:', apiSaveData.defectCode);
        }
      }

      const thermalImageIdForRefresh =
        this.thermalImageIdForSave ??
        this.solarThermalImageId ??
        this.$store.getters['dashboard/getSelectedThermalImageId'];
      const reportIdForUpdate = this.reportId;
      const reportIdForCreate = this.solarExamineReportId;

      // AI 결함 확인 로직
      if (apiSaveData.detectWay === 'AI' && apiSaveData.mlDefectConfirmed === 'NO') {
        apiSaveData.mlDefectConfirmed = 'YES';
      }

      try {
        if (this.isEdit) {
          this.saveCurrentRectanglePosition();
          await api.update(`/module-defects/${apiSaveData.id}`, apiSaveData, true);
          await this.finalizeDefectSave(apiSaveData.id);
          await this.refreshAfterDefectChange({
            solarThermalImageId: thermalImageIdForRefresh,
            reportId: reportIdForUpdate,
            changeType: 'update'
          });
        } else {
          this.saveCurrentRectanglePosition();
          const response = await api.create('/module-defects', apiSaveData);
          const createdId = this.extractDefectIdFromSaveResponse(response) ?? apiSaveData.id;
          await this.finalizeDefectSave(createdId);
          await this.refreshAfterDefectChange({
            solarThermalImageId: thermalImageIdForRefresh,
            reportId: reportIdForCreate,
            changeType: 'create'
          });
        }
      } catch (err) {
        this.$eventBus.$emit('alert-on', {
          on: true,
          message: `${err.message}`,
          status: 'error'
        });
        return;
      } finally {
        this.close();
      }
    },
    deleteItem() {
      const thermalImageIdForRefresh =
        this.thermalImageIdForSave ??
        this.solarThermalImageId ??
        this.$store.getters['dashboard/getSelectedThermalImageId'];
      const reportIdForRefresh = this.reportId ?? this.solarExamineReportId;
      const seq = this.defect.seq ?? this.defect.id;
      if (!seq) return;

      const rows = this.$store.state.dashboard?.wfsDefectRows || [];
      const removedIndex = rows.findIndex(d => Number(d.seq) === Number(seq));
      const removedRow = removedIndex >= 0 ? { ...rows[removedIndex] } : null;

      if (removedRow) {
        this.$store.commit('dashboard/removeWfsDefectRow', seq);
      }
      DotSolarMap.removeDefectMarkersBySeq(seq);

      api
        .delete(`/moduleDefects/${seq}`)
        .then(() => {
          invalidateModuleDefectDetailCache(seq);
          this.$eventBus.$emit('alert-on', {
            on: true,
            message: '결함이 정상적으로 삭제 되었습니다.',
            status: 'success'
          });
          this.refreshAfterDefectChange({
            solarThermalImageId: thermalImageIdForRefresh,
            reportId: reportIdForRefresh,
            changeType: 'delete'
          });
          this.close();
        })
        .catch((err) => {
          if (removedRow) {
            this.$store.commit('dashboard/insertWfsDefectRow', {
              row: removedRow,
              index: removedIndex
            });
          }
          this.$eventBus.$emit('alert-on', {
            on: true,
            message: `${err.message}`,
            status: 'error'
          });
        });
    },
    async handleMainDefectChange(evt) {
      if (evt) {
        this.updateSelectedDefectType({mainOrSub: 'main', Type: evt});
      }
    },
    async handleSubDefectChange(evt) {
      if (evt) {
        this.updateSelectedDefectType({mainOrSub: 'sub', Type: evt});
      }
    },
    // 현재 사각형 위치를 store에 저장
    saveCurrentRectanglePosition() {
      console.log('[DEFECT INFO CARD] defectMarker ref 존재 여부:', !!this.$refs.defectMarker);

      if (this.$refs.defectMarker && this.$refs.defectMarker.defectDrawer) {
        const defectDrawer = this.$refs.defectMarker.defectDrawer;

        // d3를 사용해서 현재 사각형 위치를 가져오기
        const activeGroup = d3.selectAll('g.active');
        const currentRect = activeGroup.select('rect.rect-main');
        const r = activeGroup.select('rect.rect-main');
        const rBB = r.node().getBBox();
        console.log('[DEFECT INFO CARD] rBB:', rBB);
        console.log('[DEFECT INFO CARD] d3로 찾은 active group:', activeGroup);
        console.log('[DEFECT INFO CARD] d3로 찾은 rect:', currentRect);

        if (!currentRect.empty()) {
          // d3를 사용해서 현재 위치 읽기
          const currentX = parseFloat(currentRect.attr('x'));
          const currentY = parseFloat(currentRect.attr('y'));
          const currentWidth = parseFloat(currentRect.attr('width'));
          const currentHeight = parseFloat(currentRect.attr('height'));

          console.log('[DEFECT INFO CARD] d3로 읽어온 실제 사각형 위치:', {
            x: currentX,
            y: currentY,
            width: currentWidth,
            height: currentHeight
          });

          // defectDrawer.Rect와 비교
          console.log('[DEFECT INFO CARD] defectDrawer.Rect 위치:', {
            x: defectDrawer.Rect.x0,
            y: defectDrawer.Rect.y0,
            width: defectDrawer.Rect.width,
            height: defectDrawer.Rect.height
          });

          // 저장 전 현재 store 상태 확인
          const currentSavedRect = this.$store.getters['defect/getCurrentReportDefaultRect'];

          // d3로 읽어온 실제 위치로 저장
          this.$store.dispatch('defect/saveDefaultRect', {
            x: currentX,
            y: currentY,
            width: currentWidth,
            height: currentHeight
          });

          // 저장 후 store 상태 확인
          setTimeout(() => {
            const afterSavedRect = this.$store.getters['defect/getCurrentReportDefaultRect'];
          }, 100);

          console.log('[DEFECT INFO CARD] ✅ 사각형 위치 저장 성공');
        } else {
          console.log('[DEFECT INFO CARD] ❌ d3로 active rect를 찾을 수 없음');
          console.log('[DEFECT INFO CARD] state.id:', defectDrawer.state.id);
        }
      } else {
        console.log('[DEFECT INFO CARD] ❌ defectMarker 또는 defectDrawer가 없어서 저장 불가');
        console.log('[DEFECT INFO CARD] this.$refs.defectMarker:', this.$refs.defectMarker);
        if (this.$refs.defectMarker) {
          console.log('[DEFECT INFO CARD] this.$refs.defectMarker.defectDrawer:', this.$refs.defectMarker.defectDrawer);
        }
      }
    },
    updateLocalDefectDesc(evt) {
      // 메모 항목에 입력된 값이 defectDesc로 서버에 전송되어야 함
      console.log('[DEFECT INFO CARD] updateLocalDefectDesc 호출:', {
        inputValue: evt,
        currentDefectFormMemo: this.defectFormMemo
      });
      this.updateDefectForm({field: 'memo', value: evt});
    }
  },
  props: {
    resetFlag: {
      type: Boolean,
      default: false
    },
    resetOptions: {
      type: Object,
      default: () => ({})
    }
  }
};
</script>

<style scoped>
.defect-info-card-root {
  min-height: 0;
}

.defect-info-card-body {
  overflow-y: auto;
}

/* 결함 상세 설명: 비어 있거나 짧을 때도 세로 칸을 유지해 로딩·네비 시 레이아웃 점프 완화 */
.defect-desc-readout {
  line-height: 1.35;
  word-break: break-word;
  min-height: 1.25rem;
}

.defect-info-card-actions {
  row-gap: 4px;
}

.defect-info-card-action-btn {
  min-height: 40px !important;
  height: 40px !important;
  font-size: 0.875rem !important;
  font-weight: 600;
  padding-left: 16px !important;
  padding-right: 16px !important;
}

/* secondary 기본보다 한 단계 밝게 — 다크에서 Array/String/Module 라인 가독성 */
.defect-info-panel-meta {
  color: color-mix(in srgb, var(--v-text-base) 72%, transparent);
  line-height: 1.35;
}

/*
 * 색: main.css `.thermal-stat-value--*`
 * 간격·글자 크기: DotSolarMapBox.vue 와 동일(지도 미로드 시에도 결함 카드만으로 동일 UI)
 */
</style>

<style>
.thermal-stats-compact-row {
  gap: 8px 12px;
  row-gap: 4px;
}

.thermal-stats-compact-item {
  font-size: 0.8125rem;
  line-height: 1.35;
}

.thermal-stats-compact-value {
  font-size: 0.9375rem;
  line-height: 1.25;
}
</style>
